import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { AlertCircle, CheckCircle, Eye, EyeOff, Loader2 } from "lucide-react";
import { useState } from "react";
import { RivaledFullLogo } from "../components/logos/RivaledFullLogo";
import { Button, Input } from "../components/ui";
import { authClient } from "../lib/auth-client";

export const Route = createFileRoute("/reset-password")({
  component: ResetPasswordPage,
  validateSearch: (search: Record<string, unknown>) => ({
    token: (search.token as string) ?? "",
    error: (search.error as string) ?? "",
  }),
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const { token, error: urlError } = Route.useSearch();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(
    urlError === "INVALID_TOKEN" ? "This reset link has expired or is invalid." : null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (!token) {
      setError("Missing reset token. Please request a new reset link.");
      return;
    }

    setIsLoading(true);

    try {
      const { error: resetError } = await authClient.resetPassword({
        newPassword: password,
        token,
      });

      if (resetError) {
        setError(resetError.message ?? "Failed to reset password");
      } else {
        setSuccess(true);
      }
    } catch {
      setError("Unable to reach the server. Check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-surface-950 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_0%,rgba(38,217,181,0.06),transparent_60%)]" />
        <div className="absolute inset-0 noise-overlay" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="relative w-full max-w-md mx-4"
      >
        <div className="flex justify-center mb-10">
          <Link to="/">
            <RivaledFullLogo className="h-10 opacity-80 hover:opacity-100 transition-opacity" />
          </Link>
        </div>

        <div className="relative rounded-2xl bg-surface-900/80 border border-surface-700/50 backdrop-blur-sm overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-500/40 to-transparent" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(38,217,181,0.04)_0%,_transparent_60%)] pointer-events-none" />

          <div className="relative px-8 py-10">
            {success ? (
              <div className="text-center space-y-4">
                <div className="flex justify-center">
                  <div className="flex items-center justify-center size-12 rounded-full bg-brand-500/10 border border-brand-500/20">
                    <CheckCircle className="size-6 text-brand-400" />
                  </div>
                </div>
                <h1 className="text-2xl font-display font-bold tracking-tight text-white uppercase">
                  Password Reset
                </h1>
                <p className="text-sm text-surface-400">
                  Your password has been updated successfully.
                </p>
                <Button
                  variant="primary"
                  onClick={() => navigate({ to: "/sign-in" })}
                  className="w-full py-2.5 mt-2"
                >
                  Sign In
                </Button>
              </div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <h1 className="text-2xl font-display font-bold tracking-tight text-white uppercase">
                    New Password
                  </h1>
                  <p className="text-sm text-surface-400 mt-2">
                    Choose a new password for your account
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                    label="New Password"
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    autoFocus
                    placeholder="At least 8 characters"
                    minLength={8}
                    suffix={
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="pointer-events-auto text-surface-500 hover:text-surface-300 transition-colors"
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                      </button>
                    }
                  />

                  <Input
                    label="Confirm Password"
                    id="confirm-password"
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    placeholder="Confirm your password"
                    minLength={8}
                  />

                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-error-500/10 border border-error-500/20 text-error-400 text-sm"
                    >
                      <AlertCircle className="size-4 shrink-0" />
                      {error}
                    </motion.div>
                  )}

                  <Button
                    type="submit"
                    variant="primary"
                    disabled={isLoading || !token}
                    className="w-full py-2.5"
                  >
                    {isLoading ? <Loader2 className="size-4 animate-spin" /> : "Reset Password"}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
